import * as React from 'react';

import { ResourceRelationsService, ALERT, IAccessesInitDataReply } from '../services/ResourceRelationsService';
import { RequestControl, IRelationRowTable, IAllRequest, IEmployeeEntity } from './Relations/RequestControl';
import lang from './Relations/RelationsLang';
import { Dialog, DialogContent, Button, DialogActions } from '@material-ui/core';
import AccessRelationEditRow, { IEditingAccessRelationData, IEditRowControlResult } from './RelationAccesses/AccessRelationEditRow';
import { IAccessRelationData, IRolePerrmission, IAccessRelationRawData } from './RelationAccesses/AccessDomainModels';

import AccessInsertForm from './RelationAccesses/AccessInsertForm';
import UserAccessesWindow from './RelationAccesses/UserAccessesWindow';
import AccessUserAccessesService, { IUserAccessDataRow, IDictionary } from './RelationAccesses/AccessUserAccessesService';
import { AccessCheckExcludedRolePermissions } from './RelationAccesses/AccessCheckExcludedRolePermissions';
import { StylesProvider, createGenerateClassName } from '@material-ui/core/styles';
import moment from "moment";
import { IUserAccessesWindowConfig } from './RelationAccesses/UserAccessesWindow';
import HtmlRootControl from './Relations/HtmlRootControl';
import { IConfig } from './RelationAccesses/IModels';
import { OptionType } from '../controls/EmployeesComboboxMI';
import { LogFiltersBuildier } from './Relations/utils/LogFiltersBuildier';
import { IIssue } from './Relations/IssueForRelations';
import { SensitiveDataAddScopeDialog } from './RelationAccesses/HtmlEditor/SensitiveDataAddScopeDialog';
import setDefaultOptionsLoader from './Relations/DefaultResourceOptionsLoader';
import DialogTitleWithClose from '../components/DialogTitleWithClose';
import AccessRequestContent from './RelationAccesses/AccessRequestContent';

import "./Relations/Relations.css";
import UserAuthorizationsPreviewWindow, { IUserAuthorizationsPreviewWindowConfig } from './RelationAccesses/UserAuthorizationsPreviewWindow';

export interface IAccessesData {
	employees: IEmployeeEntity[]
}

const generateClassName = createGenerateClassName({
	productionPrefix: 'accessesRelationControl',
	disableGlobal: true,
});

export interface IProps {

	onSet?: (v: IAccessesData) => void,

	name: string;
	value: string;
	issue: IIssue;

	forAcceptant: boolean;
	subordinatesTree: boolean;

	defaultEmployee: string;
	defaultApplications: string;
	defaultServices: string;

	blockInsert: boolean;
	blockEditRows: boolean;
	blockCloneRows: boolean;
	blockDeleteRows: boolean;
	blockChangeEmployees: boolean;

	currentAccessesHiddenInsertButton: boolean;
	currentAccessesHiddenEditButton: boolean;
	currentAccessesFilterId: string;
	currentAccessesTable: IUserAccessesWindowConfig;

	currentAuthorizationsButton: IUserAuthorizationsPreviewWindowConfig;

	editCurrentEmployee: boolean;

	config: IConfig,
}

interface IState {
	logSystemEnabled: boolean;

	editingRowData?: IEditingAccessRelationData;
	editingRowDataResult?: IEditRowControlResult;

	rowsPerUsers: IDictionary<IUserAccessDataRow[]>;
	loading: IDictionary<boolean>;

	selectedEmployees: OptionType[];
	selectedApplications: OptionType[];
	selectedServices: OptionType[],

	rowsWithSensitiveData: (IAccessRelationRawData | IAccessRelationData)[];
}

export default class AccessRelationControl extends React.Component<IProps, IState>
{
	constructor(props) {
		super(props);
		
		this.state = {
			logSystemEnabled: false,
			editingRowData: null,
			editingRowDataResult: null,

			rowsPerUsers: {},
			loading: {},

			selectedEmployees: [],
			selectedApplications: [],
			selectedServices: [],

			rowsWithSensitiveData: [],
		};
		
		this.resourceRelationsService = new ResourceRelationsService();
		this.table = React.createRef();
		this.insertForm = React.createRef<AccessInsertForm>();
		this.rootControl = React.createRef<HTMLElement>();
	}

	resourceRelationsService: ResourceRelationsService;
	table: React.RefObject<RequestControl>;
	insertForm: React.RefObject<AccessInsertForm>;
	rootControl: React.RefObject<HTMLElement>;

	componentDidMount() {

		this.resourceRelationsService.getAccessesInitData(this.props.issue.id, (data: IAccessesInitDataReply) => {

			let accesses: IAccessRelationData[] = [];
			if (!this.props.value) {
				accesses = this.parseTableData(data);
			}
			else {
				var tableResult: IAccessRelationData[] = JSON.parse(this.props.value);
				accesses = tableResult;
			}

			var defaultUsers: OptionType[] = [null];

			if (accesses.length == 0) {
				defaultUsers = this.getDefaultUsers(data);
			}

			var usersToLoad = defaultUsers.concat(accesses.map(x => {
				if (!x?.employee?.id)
					return null;
				return {
					value: x.employee.id.toString(),
					label: x.employee.name,
					kind: "Employee",
				}
			}))
			this.loadUsers(usersToLoad);

			this.setState({
				logSystemEnabled: data.logSystemEnabled,
				selectedEmployees: defaultUsers.filter(x => !!x),
			});
		});

		this.setDefaultApplications();
		this.setDefaultServices();
		
		HtmlRootControl(this.rootControl, {
			canSubmit: () => {
				if (this.insertForm.current && this.insertForm.current.hasChanges()) {
					return {
						warningMessage: lang.accessControlHasChanges(),
					};
				};
			}
		});
	}

	setDefaultApplications() {

		setDefaultOptionsLoader({
			defaultResources: this.props.defaultApplications,
			resourcesData: this.props.issue.businessApplicationsAssets,
			kind: "Application",
			filterId: this.props.config.fields.application.filterId,
			maxRows: this.props.config.fields.application.maxRows,
			multiple: this.props.config.fields.application.multiple,

			callback: (items) => {
				this.setState({ selectedApplications: items });
			}
		});
	}

	setDefaultServices() {

		setDefaultOptionsLoader({
			defaultResources: this.props.defaultServices,
			resourcesData: this.props.issue.linkedServiceAssets,
			kind: "ServiceAsset",
			filterId: this.props.config.fields.service.filterId,
			maxRows: this.props.config.fields.service.maxRows,
			multiple: this.props.config.fields.service.multiple,

			callback: (items) => {
				this.setState({ selectedServices: items });
			}
		});
	}

	getDefaultUsers(data: IAccessesInitDataReply): OptionType[] {
		let defaultUser: OptionType[] = [];

		if (this.props.defaultEmployee === "<employeeAssets>") {
			defaultUser = this.props.issue.employeeAssets.map(x => {
				return {
					value: x.id.toString(),
					label: x.label,
					kind: "Employee",
				};
			});
		}
		if (this.props.defaultEmployee === "<current>" && data.currentUser) {
			defaultUser.push({
				value: data.currentUser.id.toString(),
				label: data.currentUser.name,
				kind: "Employee",
			});
		}

		if (!this.props.config.fields.employee.multiple) {
			return [defaultUser[0]]
		}
		if (defaultUser.length == 0) {
			return [null];
		}
		return defaultUser;
	}

	loadUsers(users: OptionType[]) {
		var rowsPerUsers = { ...this.state.rowsPerUsers }
		var loading = { ...this.state.loading }

		for (var i = 0; i < users.length; i++) {
			this._loadUser(loading, rowsPerUsers, users[i]);
		}

		this.setState({
			rowsPerUsers: rowsPerUsers,
			loading: loading
		});
	}

	_loadUser(loading: IDictionary<boolean>, rowsPerUsers: IDictionary<IUserAccessDataRow[]>, user: OptionType) {
		if (!user?.value)
			return;

		if (loading[user.value])
			return;

		if (rowsPerUsers[user.value])
			return;

		loading[user.value] = true;

		let fb = new LogFiltersBuildier(this.props.currentAccessesTable.filters, this.props.issue);

		new AccessUserAccessesService().load(
			user.value,
			this.props.currentAccessesFilterId,
			fb.buildForServices(),
			fb.buildForApplications(),
			(data) => {
				this._loadedUser(user.value, data);
			});
	}

	_loadedUser(id: string, data: IUserAccessDataRow[]) {
		var rowsPerUsers = { ...this.state.rowsPerUsers }
		var loading = { ...this.state.loading }

		loading[id] = false;
		rowsPerUsers[id] = data;

		this.setState({
			rowsPerUsers: rowsPerUsers,
			loading: loading
		});
	}

	parseTableData(dataFromApi: IAccessesInitDataReply): IAccessRelationData[] {
		var result: IAccessRelationData[] = null;
		if (dataFromApi.value && Array.isArray(dataFromApi.value)) {
			 result = dataFromApi.value.map(initItemData => {
				let role: IRolePerrmission = null;
				if (initItemData.role) {
					role = dataFromApi.rolePermissionsForValues.filter(r => r.id == initItemData.role.id)[0]
					if (!role) {
						role = {
							id: -1,
							name: initItemData.role.name,
							administrativeRights: false,
							description: "",
							excludedRolePermissions: [],
							sensitiveData: false,
							reasonForProcessingSensitiveData: "",
						}
					}
				}
				
				let item: IAccessRelationData = {
					id: initItemData.id,
					employee: initItemData.employee,
					resource: initItemData.resource,
					role: role,
					from: initItemData.from ? moment(initItemData.from) : null,
					beforeTo: initItemData.beforeTo ? moment(initItemData.beforeTo) : null,
					to: initItemData.to ? moment(initItemData.to) : null,

					login: initItemData.login,
					environment: initItemData.environment,
					password: initItemData.password,
					passwordExist: initItemData.passwordExist,
					personAccountDescription: initItemData.personAccountDescription,
					personAccountType: initItemData.personAccountType,
					company: initItemData.company,
					telephone: initItemData.telephone,
					email: initItemData.email,
					location: initItemData.location,
					referenceNumber: initItemData.referenceNumber,
					systemName: initItemData.systemName,

					description: initItemData.description,

					excluded: initItemData.excluded,
					deleted: initItemData.deleted,
					relationId: initItemData.relationId,
					key: null,
					errorMessages: initItemData.errorMessages,
				};
				return item;
			});

			this.checkAccessesInLog(result);
			this.table.current.push(result);
		}

		return result;
	}
	
	clear() {
		this.insertForm.current.clear();
	}

	checkAccessesInLog(table: IAccessRelationData[]) {
		this.resourceRelationsService.checkAccessesInLog(table, () => {
			let rows: IAccessRelationData[] = this.table.current.getRows();
			for (let i = 0; i < rows.length; i++) {
				let row = rows[i];
				if (!row.deleted)
					row.alert = this.resourceRelationsService.getAccessAlertCode(row);
			}
			this.table.current.setRows(rows)
		})
	}

	render() {
		let fields = this.props.config.fields;
		return (<StylesProvider generateClassName={generateClassName} >
				<div className="root-relation-resource">
					<AccessInsertForm ref={this.insertForm}
						issue={this.props.issue}
						selectedEmployees={this.state.selectedEmployees}
						onChangeSelectedEmployees={(v) => {
							this.setState({ selectedEmployees: v })
							this.loadUsers(v);
						}}
						employeesFilters={{
							filterId: fields.employee.filterId,
							forAcceptant: this.props.forAcceptant,
							subordinatesTree: this.props.subordinatesTree,
							maxRows: fields.employee.maxRows,
						}}

						selectedApplications={this.state.selectedApplications}
						onChangeSelectedApplications={(v) => this.setState({ selectedApplications: v })}

						selectedServices={this.state.selectedServices}
						onChangeSelectedServices={(v) => this.setState({ selectedServices: v })}
					
						table={this.table.current}

						checkAccessesInLog={this.checkAccessesInLog.bind(this)}
					
						blockInsert={this.props.blockInsert}

						rowsPerUsers={this.state.rowsPerUsers}
						loading={this.state.loading}

						blockChangeEmployees={this.props.blockChangeEmployees}

						hiddenCurrentAccessesButton={this.props.currentAccessesHiddenInsertButton}
						currentAccessesTable={this.props.currentAccessesTable}

						currentAuthorizationsButton={this.props.currentAuthorizationsButton}

						config={this.props.config}
					/>
					<div>
						{this.renderTable()}
					</div>
				</div>
			</StylesProvider>);
	}

	handleEditClick(event: any, row: IAccessRelationData)
	{
		let r = { ...row, copy: false };
		this.setState({ editingRowData: r });
	}

	handleCopyClick(event: any, row: IAccessRelationData) {
		let r = { ...row, copy: true };
		this.setState({ editingRowData: r });
	}

	handleTableInit(requests: IAllRequest) {
		var rows: IAccessRelationData[] = [];
		Object.values(requests).map(x => rows = rows.concat(x.request as IAccessRelationData[]));
		
		var emptyAlerts = rows.filter(r => !r.alert);
		if (emptyAlerts.length != 0)
			this.checkAccessesInLog(emptyAlerts);
	}
	
	renderTable() {
		const fields = this.props.config.fields;
		return <section className="access-relation-control" ref={this.rootControl}>
			<h6>
				{lang.get("applicationScope")}
			</h6>
			<AccessRequestContent
				table={this.table}

				name={this.props.name}
				value={this.props.value}

				config={this.props.config}
				blockCloneRows={this.props.blockCloneRows}
				blockDeleteRows={this.props.blockDeleteRows}
				blockEditRows={this.props.blockEditRows}
				blockInsert={this.props.blockInsert}
				editCurrentEmployee={this.props.editCurrentEmployee}
				forAcceptant={this.props.forAcceptant}
				subordinatesTree={this.props.subordinatesTree}
				
				onInitData={this.handleTableInit.bind(this)}
				onEditClick={this.handleEditClick.bind(this)}
				onCopyClick={this.handleCopyClick.bind(this)}

				rowsPerUsers={this.state.rowsPerUsers}

				employeeRelationsWindow={(employe: OptionType) => {

					if (!employe)
						return null;

					return <>
						{this.props.currentAccessesHiddenEditButton && <UserAccessesWindow
							user={employe}
							rowsPerUsers={this.state.rowsPerUsers[employe.value]}
							loading={this.state.loading[employe.value]}
							canInsert={true}
							onInsert={(items) => {
								let sensitiveData = items.filter(x => this.openSensitiveDataDialog(x.role));
								let withoutSensitiveData = items.filter(x => !this.openSensitiveDataDialog(x.role));

								this.table.current.push(withoutSensitiveData);
								this.setState({ rowsWithSensitiveData: sensitiveData });
							}}
							blockedRelationIds={() => this.table.current?.getRows().filter(x => !x.deleted && x.relationId).map(x => x.relationId) ?? []}
							config={this.props.currentAccessesTable}
						/>}
						<UserAuthorizationsPreviewWindow
							user={employe}
							config={this.props.currentAuthorizationsButton}
						/>
					</>
				}}

				changeEmployee={(rawRow: IRelationRowTable, employee: OptionType) => {

					this.loadUsers([employee]);

					let row = rawRow as IAccessRelationData;
					let currentUserAccesses: IUserAccessDataRow[] = null;
					let employeeEntity: IEmployeeEntity = null;
					if (employee) {
						currentUserAccesses = this.state.rowsPerUsers[employee.value];
						employeeEntity = {
							id: parseInt(employee.value),
							name: employee.label,
							label: employee.label,
							kind: "Employee",
							type: null,
						}
					}

					let excluded = new AccessCheckExcludedRolePermissions(this.table.current, currentUserAccesses)
						.checkExcludedRolePermissions(row.role);

					return { ...row, employee: employeeEntity, alert: ALERT.Loading, excluded: excluded };
				}}

				onchangedEmployee={(rows, emp) => this.checkAccessesInLog(rows as IAccessRelationData[])}

				onChangedEmployees={emp => {
					this.props.onSet({
						employees: emp,
					})
				}}
			/>
			{this.renderEditingRow()}
			<SensitiveDataAddScopeDialog
				issueId={this.props.issue?.id}
				relations={this.state.rowsWithSensitiveData}
				onAccept={(r) => {
					let result = r as IAccessRelationRawData[];
					this.saveSensitiveDataItems(result);
					this.setState({ rowsWithSensitiveData: [] });
				}}
				onClose={() => this.setState({ rowsWithSensitiveData: [] })}
				settings={{
					primaryTemplateName: fields.sensitiveData?.primaryTemplateName,
					secondaryTemplateName: fields.sensitiveData?.secondaryTemplateName
				}}
			/>
		</section>;
	}

	handleSaveRow() {
		const data = this.state.editingRowDataResult;
		if (data) {
			if (this.state.editingRowData.copy) {
				let itemToPush: IAccessRelationRawData = {
					description: data.description,
					employee: this.state.editingRowData.employee,
					environment: data.environment,
					excluded: [],
					from: data.from,
					login: data.login,
					password: data.password,
					to: data.to,
					personAccountDescription: data.personAccountDescription,
					personAccountType: data.personAccountType,
					company: data.company,
					telephone: data.telephone,
					email: data.email,
					location: data.location,
					referenceNumber: data.referenceNumber,
					resource: this.state.editingRowData.resource,
					systemName: data.systemName,
					role: data.role,
					documentVariables: { ...this.state.editingRowData.documentVariables },

					errorMessages: null,
					passwordExist: false,
				};
				if (this.openSensitiveDataDialog(itemToPush.role))
					this.setState({ rowsWithSensitiveData: [itemToPush] });
				else
					this.saveCopyRow([itemToPush]);
			}
			else {
				this.edit(data);
			}
		}
	}

	saveCopyRow(itemsToPush: IAccessRelationRawData[])
	{
		let itemToPush = itemsToPush[0];
		if (itemToPush) {
			let currentUserAccesses: IUserAccessDataRow[] = null;
			if (itemToPush.employee) {
				currentUserAccesses = this.state.rowsPerUsers[itemToPush.employee.id];
			}

			itemToPush.excluded = new AccessCheckExcludedRolePermissions(this.table.current, currentUserAccesses)
				.checkExcludedRolePermissions(itemToPush.role, itemsToPush);

			this.checkAccessesInLog(itemsToPush as IAccessRelationData[]);
			this.table.current.push(itemsToPush);
		}
		
		this.setState({
			editingRowData: null,
		});
	}

	openSensitiveDataDialog(role: IRolePerrmission): boolean {
		let fields = this.props.config.fields;
		return role?.sensitiveData
			&& (!!fields.sensitiveData?.primaryTemplateName || !!fields.sensitiveData?.secondaryTemplateName);
	}

	edit(data: IEditRowControlResult)
	{
		console.log("openSensitiveDataDialog");
		if (this.openSensitiveDataDialog(data.role)) {
			this.setState({
				rowsWithSensitiveData: [{
					company: data.company,
					description: data.description,
					employee: this.state.editingRowData.employee,
					environment: data.environment,
					errorMessages: this.state.editingRowData.errorMessages,
					excluded: this.state.editingRowData.excluded,
					from: data.from,
					login: data.login,
					password: data.password,
					passwordExist: this.state.editingRowData.passwordExist,
					personAccountDescription: data.personAccountDescription,
					personAccountType: data.personAccountType,
					telephone: data.telephone,
					email: data.email,
					location: data.location,
					referenceNumber: data.referenceNumber,
					resource: this.state.editingRowData.resource,
					systemName: data.systemName,
					role: data.role,
					to: data.to,
					documentVariables: this.state.editingRowData.documentVariables,
				}]
			});
		}
		else
			this.saveEditRow(null, false);
	}

	saveEditRow(itemsToPush: IAccessRelationRawData[], isSensitiveData: boolean)
	{
		var data = this.state.editingRowDataResult;

		let rows: IAccessRelationData[] = this.table.current.getRows();
		for (let i = 0; i < rows.length; i++) {
			let row = rows[i];
			if (!row.deleted && row.key == this.state.editingRowData.key) {

				let currentUserAccesses: IUserAccessDataRow[] = null;
				if (row.employee) {
					currentUserAccesses = this.state.rowsPerUsers[row.employee.id];
				}

				let excluded = new AccessCheckExcludedRolePermissions(this.table.current, currentUserAccesses)
					.checkExcludedRolePermissions(row.role);

				rows[i] = {
					...rows[i],
					...data,
					alert: ALERT.Loading,
					excluded: excluded,
					errorMessages: null,
				};

				if (itemsToPush && itemsToPush[0]) {
					rows[i].documentVariables = itemsToPush[0].documentVariables;
				}
				break;
			}
		}

		this.checkAccessesInLog(rows);
		this.table.current.setRows(rows);

		this.setState({
			editingRowData: null,
		});
	}

	saveSensitiveDataItems(itemsToPush: IAccessRelationRawData[]) {
		if (this.state.editingRowData) {
			if (this.state.editingRowData.copy) {
				this.saveCopyRow(itemsToPush);
			}
			else {
				this.saveEditRow(itemsToPush, true);
			}
			return;
		}
		// dane z "Aktualnych dostępów"
		this.table.current.push(itemsToPush);
	}

	renderEditingRow() {
		if (!this.state.editingRowData)
			return null;

		return <>
			<Dialog
				open={true}
				onClose={() => { this.setState({ editingRowData: null }) }}
				disableBackdropClick={true}
			>
				<DialogTitleWithClose
					onClose={() => { this.setState({ editingRowData: null }) }}
				>
					{!this.state.editingRowData.copy && <span>{lang.editing()}</span>}
					{this.state.editingRowData.copy && <span>{lang.copying()}</span>}
					{' '}
					{this.state.editingRowData.resource.label}
				</DialogTitleWithClose>
				<DialogContent>
					<AccessRelationEditRow
						row={this.state.editingRowData}
						onChange={(v) => {
							this.setState({ editingRowDataResult: v });
						}}
						appName={this.state.editingRowData.resource.label}

						config={this.props.config}
					/>
				</DialogContent>
				<DialogActions>
					<Button color="default"
						onClick={() => { this.setState({ editingRowData: null }) }}
					>
						{lang.get("cancel")}
					</Button>
					<Button onClick={this.handleSaveRow.bind(this)} color="primary" variant="contained" >
						{lang.get("save")}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	}
}